import React, {useCallback, useEffect, useMemo, useState} from 'react';
import secureLocalStorage from 'react-secure-storage';
import {requestFCMPermissionAndToken} from '../firebase/fcm';

// A lightweight UI to guide users to enable notifications
// States:
// - 'granted': nothing shown
// - 'default': show CTA to enable notifications
// - 'denied': show instructions to enable from browser settings
export default function NotificationPermissionManager({ onGranted }) {
    const [permission, setPermission] = useState(
        typeof Notification !== 'undefined' ? Notification.permission : 'default'
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [diagnostic, setDiagnostic] = useState('');
    const [success, setSuccess] = useState('');
    const [isVisible, setIsVisible] = useState(true);
    const [isHovered, setIsHovered] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    // Compute a concise environment status to avoid scattered conditionals
    const getStatus = useCallback(() => {
        const hasNotification = typeof Notification !== 'undefined';
        const hasSW = 'serviceWorker' in navigator;
        const hasPush = 'PushManager' in window;
        if (!hasNotification || !hasSW || !hasPush) return 'unsupported';
        const host = window.location.hostname;
        const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '::1';
        const isSecure = window.isSecureContext || window.location.protocol === 'https:' || isLocalhost;
        if (!isSecure) return 'insecure';
        return Notification.permission; // 'default' | 'granted' | 'denied'
    }, []);

    // Helper to request permission and get token
    const requestPermission = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            setSuccess('');
            const status = getStatus();
            // Centralized handling based on environment status
            if (status === 'unsupported') {
                setError('This browser/context does not support Web Push (Notifications/Service Worker/Push API missing).');
                return;
            }
            if (status === 'insecure') {
                setDiagnostic('Site is not served over HTTPS. Push notifications require HTTPS (except localhost).');
                return;
            }
            if (status === 'denied') {
                setError('Notifications are blocked for this site. Enable them in browser settings and reload, then retry.');
                return;
            }
            // 'default' | 'granted' → delegate to FCM helper which requests permission if needed and fetches token
            const token = await requestFCMPermissionAndToken();
            setPermission(typeof Notification !== 'undefined' ? Notification.permission : 'default');
            if (token) {
                secureLocalStorage.setItem('fcmToken', token);
                if (onGranted) onGranted(token);
                setSuccess('Notifications enabled.');
            } else {
                if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
                    setError('Please allow notifications in the browser prompt to receive alerts.');
                } else {
                    setError('Permission granted but failed to retrieve FCM token. Please check Service Worker registration, scope, and VAPID key.');
                }
            }
        } catch (e) {
            setError('Failed to enable notifications.');
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [getStatus, onGranted]);

    // React to permission changes: prefer Permissions API, fallback to light polling
    useEffect(() => {
        let cleanup = () => {};
        (async () => {
            try {
                if (navigator.permissions && navigator.permissions.query) {
                    const status = await navigator.permissions.query({ name: 'notifications' });
                    const update = () => {
                        if (typeof Notification !== 'undefined') {
                            setPermission(Notification.permission);
                        }
                    };
                    update();
                    status.onchange = update;
                    cleanup = () => { status.onchange = null; };
                    return;
                }
            } catch (e) {
                // ignore and fallback
            }
            const id = setInterval(() => {
                if (typeof Notification !== 'undefined') {
                    setPermission((prev) => (prev !== Notification.permission ? Notification.permission : prev));
                }
            }, 10000);
            cleanup = () => clearInterval(id);
        })();
        return () => cleanup();
    }, []);

    // Auto-hide after 3 seconds
    useEffect(() => {
        if(isVisible){
            const timer = setTimeout(() => {
                setIsVisible(false);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [isVisible]);

    const browserHelp = useMemo(() => {
        const ua = navigator.userAgent || '';
        if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) {
            return { name: 'Chrome' };
        }
        if (/Edg\//.test(ua)) {
            return { name: 'Edge' };
        }
        if (/Firefox\//.test(ua)) {
            return { name: 'Firefox' };
        }
        if (/Safari\//.test(ua) && /Macintosh/.test(ua)) {
            return { name: 'Safari' };
        }
        return { name: 'your browser' };
    }, []);

    if (typeof Notification === 'undefined') return null;
    if (permission === 'granted') return null;
    if (isDismissed) return null;

    const uiMap = {
        default: {
            title: 'Enable notifications',
            subtitle: 'Turn on push notifications to receive important updates in real time.',
            baseButtonLabel: 'Allow notifications',
            loadingLabel: 'Enabling…',
            buttonStyle: { background: '#7F56D9', color: 'white' },
            showSteps: false,
            showDiagnostic: true,
            subtitleMargin: 12,
        },
        denied: {
            title: 'Notifications are blocked',
            subtitle: `Please enable notifications from ${browserHelp.name} settings to receive alerts. Direct links to browser settings cannot be opened from a web page.`,
            baseButtonLabel: "I've enabled it, retry",
            loadingLabel: 'Checking…',
            buttonStyle: { background: '#475467', color: 'white' },
            showSteps: true,
            showDiagnostic: false,
            subtitleMargin: 10,
        },
    };
    const ui = uiMap[permission] || uiMap.denied;
    const labelByLoading = { true: ui.loadingLabel, false: ui.baseButtonLabel };
    const buttonLabel = labelByLoading[String(loading)];

    return (
        <>
            {/* Full notification panel */}
            {isVisible && (
                <div
                    style={{
                        position: 'fixed',
                        top: window.innerWidth <= 768 ? 24 : 'auto',
                        bottom: window.innerWidth <= 768 ? 'auto' : 24,
                        left: window.innerWidth <= 768 ? 16 : 'auto',
                        right: window.innerWidth <= 768 ? 16 : 24,
                        zIndex: 10000,
                        maxWidth: window.innerWidth <= 768 ? 'calc(100vw - 32px)' : 360,
                        background: '#101828',
                        color: 'white',
                        padding: 16,
                        borderRadius: 12,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                        fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial',
                        transition: 'opacity 0.3s ease-in-out',
                    }}
                >
                    {/* Close button */}
                    <button
                        onClick={() => setIsDismissed(true)}
                        style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: 18,
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 4,
                            opacity: 0.7,
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = '1'}
                        onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                        title="Dismiss notification"
                    >
                        ×
                    </button>
                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
                        {ui.title}
                    </div>
                    <div style={{ fontSize: 13, opacity: 0.9, marginBottom: ui.subtitleMargin }}>
                        {ui.subtitle}
                    </div>
                    {ui.showSteps && (
                        <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 12, lineHeight: 1.4 }}>
                            Suggested steps:
                            <ol style={{ margin: '6px 0 0 16px' }}>
                                <li>Open {browserHelp.name} settings.</li>
                                <li>Go to Privacy/Site Settings → Notifications.</li>
                                <li>Find this site and set it to Allow.</li>
                                <li>Return to this page and click "I've enabled it, retry".</li>
                            </ol>
                        </div>
                    )}
                    {ui.showDiagnostic && diagnostic ? (
                        <div style={{ color: '#FDB022', fontSize: 12, marginBottom: 8 }}>{diagnostic}</div>
                    ) : null}
                    {error ? (
                        <div style={{ color: '#F97066', fontSize: 12, marginBottom: 8 }}>{error}</div>
                    ) : null}
                    {success ? (
                        <div style={{ color: '#32D583', fontSize: 12, marginBottom: 8 }}>{success}</div>
                    ) : null}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                            onClick={requestPermission}
                            disabled={loading}
                            style={{
                                ...ui.buttonStyle,
                                border: 'none',
                                borderRadius: 8,
                                padding: '8px 12px',
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            {buttonLabel}
                        </button>
                    </div>
                </div>
            )}

            {/* Red dot indicator when hidden */}
            {!isVisible && (
                <div
                    style={{
                        position: 'fixed',
                        top: window.innerWidth <= 768 ? 24 : 'auto',
                        bottom: window.innerWidth <= 768 ? 'auto' : 24,
                        right: window.innerWidth <= 768 ? 16 : 24,
                        zIndex: 10000,
                        width: 16,
                        height: 16,
                        background: '#F97066',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(249, 112, 102, 0.4)',
                        transition: 'all 0.2s ease-in-out',
                        transform: isHovered ? 'scale(1.2)' : 'scale(1)',
                    }}
                    onClick={() => setIsVisible(true)}
                    onMouseEnter={() => {
                        setIsHovered(true);
                        setIsVisible(true);
                    }}
                    onMouseLeave={() => setIsHovered(false)}
                    title="Click to show notification settings"
                />
            )}
        </>
    );
}
